import type { Archive } from "../../domain/types";
import { callLLMWithRoutes, type LlmRoute } from "../../services/llm";
import {
  evolutionFeatureId,
  resolveFeatureRoutes,
  type FeatureRoutingSettings,
} from "../../services/featureRoutes";
import { parseStateCommandResponse } from "../../services/stateCommandParser";
import { applyStateCommand } from "../../services/sendMessage/postTurn";
import {
  assembleEvolutionMessages,
  type EvolutionPlaceholders,
} from "./promptAssembler";
import type {
  EvolutionPhase,
  EvolutionPreset,
  EvolutionPromptKind,
  EvolutionRunLog,
} from "./types";

export const EVOLUTION_STAGES: EvolutionPromptKind[][] = [
  ["entry_all_focus"],
  ["item_management"],
  ["player", "npc", "monster"],
  ["misc_management"],
];
export const EQUIPMENT_NUMERIC_TEXT_GUARD =
  /(?:itemRegistry|equipment).*(?:attack|defense|affinity|rarity|sharpnessTier)|装备.*(?:攻击|防御|会心|稀有度|锐利度)/i;
export const HR_WRITE_GUARD =
  /player\.hunter\.(?:hr|hrExp)|(?:修改|增加|设置)\s*HR/i;
let presetPromise: Promise<EvolutionPreset> | null = null;

export async function loadEvolutionPreset(): Promise<EvolutionPreset> {
  presetPromise ??= fetch(
    "/r2-presets/concurrent-evolution/MH-完整重点演化.json",
  ).then(async (response) => {
    if (!response.ok)
      throw new Error(`重点演化预设加载失败 (${response.status})`);
    const preset = (await response.json()) as EvolutionPreset;
    if (
      !preset.prompts?.entry_all_focus ||
      !preset.prompts?.item_management ||
      !preset.prompts?.monster
    )
      throw new Error("重点演化预设缺少必要阶段");
    return preset;
  });
  return presetPromise;
}

function phaseFor(kind: EvolutionPromptKind): EvolutionPhase {
  return kind === "entry_all_focus"
    ? "entry"
    : kind === "item_management"
      ? "item"
      : kind === "misc_management"
        ? "misc"
        : kind;
}
function compactArchive(archive: Archive) {
  return JSON.stringify({
    player: archive.player,
    characters: archive.characters,
    monsters: archive.monsters,
    tasks: archive.tasks,
    itemRegistry: archive.itemRegistry,
    scene: archive.scene,
    gameTime: archive.gameTime,
    narrativeMemory: archive.narrativeMemory,
  });
}
function buildPlaceholders(
  archive: Archive,
  story: string,
  input: string,
  targetId?: string,
  priorOutputs: Partial<Record<EvolutionPromptKind, string[]>> = {},
  entryCreatedTargets: ReadonlySet<string> = new Set(),
): EvolutionPlaceholders {
  const player = JSON.stringify(archive.player),
    characters = JSON.stringify(archive.characters),
    monsters = JSON.stringify(archive.monsters),
    items = JSON.stringify(archive.itemRegistry),
    tasks = JSON.stringify(archive.tasks),
    targetCharacter = archive.characters[targetId ?? ""],
    targetRecord = (targetCharacter ?? {}) as unknown as Record<
      string,
      unknown
    >,
    recentConversation = archive.messages
      .filter((message) => message.role !== "system")
      .slice(-8)
      .map((message) => `${message.role}: ${message.content}`)
      .join("\n"),
    targetName = String(
      targetCharacter?.identity.name ??
        archive.monsters[targetId ?? ""]?.name ??
        "",
    ),
    targetConversation = archive.messages
      .filter(
        (message) =>
          message.role !== "system" &&
          (!targetName || message.content.includes(targetName)),
      )
      .slice(-6)
      .map((message) => `${message.role}: ${message.content}`)
      .join("\n"),
    itemStageResults =
      (priorOutputs.item_management ?? []).join("\n") ||
      "本轮物品阶段没有产生状态命令。",
    nextNpc =
      Math.max(
        0,
        ...Object.keys(archive.characters).map((id) =>
          Number(id.match(/^C(\d+)$/)?.[1] ?? 0),
        ),
      ) + 1;
  return {
    story_text: story,
    story,
    main_text: story,
    user_input: input,
    current_state: compactArchive(archive),
    player_snapshot: player,
    player_equipment: JSON.stringify(archive.player.equipped),
    player_items: items,
    player_skills: "当前存档没有独立玩家技能表；不得生成技能写入。",
    player_gongfa: JSON.stringify(archive.player.proficiency),
    player_proficiency: JSON.stringify(archive.player.proficiency),
    target_narrative_memory: JSON.stringify(archive.narrativeMemory),
    character_snapshot: targetId
      ? JSON.stringify(
          archive.characters[targetId] ??
            archive.monsters[targetId] ??
            archive.player,
        )
      : player,
    all_characters: characters,
    all_character_snapshots: characters,
    existing_character_ids: Object.keys(archive.characters).join(", ") || "无",
    all_character_ids:
      ["B1", ...Object.keys(archive.characters)].join(", ") || "B1",
    next_available_npc_id: `C${nextNpc}`,
    beast_snapshot: monsters,
    monster_snapshot: monsters,
    monsters_summary: monsters,
    all_beasts: monsters,
    all_monsters: monsters,
    item_snapshot: items,
    item_registry: items,
    item_management_results: itemStageResults,
    task_context: tasks,
    active_task: JSON.stringify(
      archive.tasks.available.find(
        (task) => task.id === archive.tasks.activeTaskId,
      ) ?? null,
    ),
    scene: JSON.stringify(archive.scene),
    scene_type: targetId && targetId !== "B1" ? "目标角色演化" : "玩家演化",
    is_offscene:
      targetId &&
      String(
        targetCharacter?.presence.location ??
          archive.monsters[targetId]?.location ??
          "",
      ) &&
      String(
        targetCharacter?.presence.location ??
          archive.monsters[targetId]?.location,
      ) !== archive.scene.location
        ? "true"
        : "false",
    is_entry_created_target: entryCreatedTargets.has(targetId ?? "")
      ? "true"
      : "false",
    current_location: archive.scene.location,
    current_time: `第${archive.gameTime.day}日 ${archive.gameTime.hour}:00`,
    game_time: JSON.stringify(archive.gameTime),
    currentTime: `第${archive.gameTime.day}日 ${archive.gameTime.hour}:00`,
    timeSinceLastEvolution: String(
      targetRecord.lastEvolutionAt == null
        ? "未知"
        : Math.max(
            0,
            archive.gameTime.day * 24 +
              archive.gameTime.hour -
              Number(targetRecord.lastEvolutionAt),
          ),
    ),
    character_id: targetId ?? "B1",
    character_name:
      targetId === "B1"
        ? archive.player.identity.name
        : String(
            archive.characters[targetId ?? ""]?.identity.name ??
              archive.monsters[targetId ?? ""]?.name ??
              targetId ??
              "",
          ),
    character_equipment: JSON.stringify(targetRecord.equipped ?? null),
    character_items: JSON.stringify(targetRecord.items ?? null),
    character_gongfa: JSON.stringify(
      targetRecord.proficiency ?? "当前档案无 NPC 熟练度结构",
    ),
    npc_biography: JSON.stringify(
      targetCharacter?.bio.biography ?? "无已存传记",
    ),
    npc_perspective_story: story,
    world_map_pois: JSON.stringify({ current: archive.scene.location }),
    world_events: JSON.stringify(archive.worldEvents),
    world_factors: JSON.stringify({
      scene: archive.scene,
      events: archive.worldEvents,
    }),
    quick_chat_all: recentConversation || "本轮没有可用的近期交谈。",
    quick_chat_npc: targetConversation || "本轮没有该 NPC 参与的近期交谈。",
    thinking_content:
      "不注入或要求隐藏思维链；仅以正文、输入、存档与显式审计规则为证据。",
    task_outcome_delta: JSON.stringify(
      archive.tasks.history.slice(-3).map(({ id, status, settlementDone }) => ({
        id,
        status,
        settlementDone,
      })),
    ),
    hunter_naming_rule:
      "使用资料库已确认的 Monster Hunter 人名、公会职务与地区称谓；未知时不造正史身份。",
    next_available_task_id: `T_G_${archive.tasks.available.length + archive.tasks.history.length + 1}`,
    target_is_explicit_minor: "false",
    character_portrait_prompt_enabled: "false",
    task_cultivation_rewards_enabled: "false",
  };
}

export function assertEvolutionResponse(
  kind: EvolutionPromptKind,
  commands: unknown[],
) {
  const structured = JSON.stringify(commands);
  if (HR_WRITE_GUARD.test(structured))
    throw new Error(`${kind} 演化越权写入 HR`);
  if (EQUIPMENT_NUMERIC_TEXT_GUARD.test(structured) && kind !== "monster")
    throw new Error(`${kind} 演化越权写入装备数值`);
}

async function runOne(
  archive: Archive,
  preset: EvolutionPreset,
  routes: LlmRoute[],
  kind: EvolutionPromptKind,
  story: string,
  input: string,
  targetId?: string,
  priorOutputs: Partial<Record<EvolutionPromptKind, string[]>> = {},
  entryCreatedTargets: ReadonlySet<string> = new Set(),
) {
  const startedAt = Date.now(),
    assembled = assembleEvolutionMessages(
      preset,
      kind,
      buildPlaceholders(
        archive,
        story,
        input,
        targetId,
        priorOutputs,
        entryCreatedTargets,
      ),
    );
  const messages = assembled.assistantPrefill
    ? [
        ...assembled.messages,
        { role: "assistant" as const, content: assembled.assistantPrefill },
      ]
    : assembled.messages;
  const response = await callLLMWithRoutes(
    routes.map((route) => ({
      ...route,
      id: `evolution:${kind}`,
      temperature: 0,
      retries: 1,
    })),
    messages,
    {
      stream: false,
      responseFormat: preset.outputFormat === "json" ? "json" : "text",
    },
  );
  const parsed = parseStateCommandResponse(response.text, phaseFor(kind));
  assertEvolutionResponse(kind, parsed.commands);
  const log: EvolutionRunLog = {
    kind,
    targetId,
    startedAt,
    finishedAt: Date.now(),
    commandCount: parsed.commands.length,
    diagnostics: parsed.diagnostics.map((item) => item.message),
    rawResponse: response.text,
  };
  return { commands: parsed.commands, log };
}

function targets(
  archive: Archive,
  kind: EvolutionPromptKind,
): Array<string | undefined> {
  if (kind === "player") return ["B1"];
  if (kind === "npc") return Object.keys(archive.characters);
  if (kind === "monster") return Object.keys(archive.monsters);
  return [undefined];
}

export async function triggerAutoEvolution(
  archive: Archive,
  story: string,
  input: string,
  route?: FeatureRoutingSettings,
): Promise<{ archive: Archive; logs: EvolutionRunLog[] }> {
  if (!route) return { archive, logs: [] };
  const preset = await loadEvolutionPreset();
  let next = structuredClone(archive);
  const logs: EvolutionRunLog[] = [];
  const priorOutputs: Partial<Record<EvolutionPromptKind, string[]>> = {};
  const entryCreatedTargets = new Set<string>();
  for (const stage of EVOLUTION_STAGES) {
    const idsBeforeStage = new Set([
      ...Object.keys(next.characters),
      ...Object.keys(next.monsters),
    ]);
    const jobs = stage.flatMap((kind) =>
      targets(next, kind).map((targetId) => ({
        kind,
        targetId,
        snapshot: structuredClone(next),
      })),
    );
    const results = await Promise.all(
      jobs.map((job) =>
        runOne(
          job.snapshot,
          preset,
          resolveFeatureRoutes(evolutionFeatureId(job.kind, route.evolutionRoutesSplit !== false), route),
          job.kind,
          story,
          input,
          job.targetId,
          priorOutputs,
          entryCreatedTargets,
        ),
      ),
    );
    for (const result of results) {
      logs.push(result.log);
      (priorOutputs[result.log.kind] ??= []).push(result.log.rawResponse);
      for (const command of result.commands)
        next = applyStateCommand(next, command);
    }
    if (stage.includes("entry_all_focus"))
      for (const id of [
        ...Object.keys(next.characters),
        ...Object.keys(next.monsters),
      ])
        if (!idsBeforeStage.has(id)) entryCreatedTargets.add(id);
  }
  return { archive: next, logs };
}
